<script setup lang="ts">
import AppGrid from "@/components/AppGrid.vue";
import { FITTABLE_DEFAULT_COL_DEF } from "@/components/composables/useGridColumnDefaults.ts";
import type { PoDetailGridSkuRow } from "@/redistribution/models/PoDetailGridSkuRow.ts";
import { useRedistributionStore } from "@/stores/redistributionStore.ts";
import type { ColDef } from "ag-grid-community";
import { storeToRefs } from "pinia";
import { computed } from "vue";
import { showPoDetailPreviousValues } from "../home/featureFlags.ts";
import {
  calculateUrgency,
  daysBetween,
  formatDate,
  urgencyLabel,
} from "./resultHelpers.ts";
const redistributionStore = useRedistributionStore();
const { poDetailGridRowDatas, updatedPOs } = storeToRefs(redistributionStore);

const purchaseOrders = computed(() => updatedPOs.value);

function getRowData(poNumber: string): PoDetailGridSkuRow[] {
  return poDetailGridRowDatas.value[poNumber] ?? [];
}

function renderWeeksOfStockCell(
  weeks: number,
  dailyRos: number,
  daysToStockout: number
): HTMLElement {
  const label = urgencyLabel(
    calculateUrgency(daysToStockout, dailyRos),
    dailyRos
  );
  const bgColor: Record<string, string> = {
    "urgency-critical": "#fdecea",
    "urgency-moderate": "#fff3cd",
    "urgency-ok": "#eafaf1",
    "urgency-na": "#f0f0f0",
  };
  const textColor: Record<string, string> = {
    "urgency-critical": "#c0392b",
    "urgency-moderate": "#856404",
    "urgency-ok": "#27ae60",
    "urgency-na": "#888888",
  };
  const bg = bgColor[label.cssClass] ?? "#f0f0f0";
  const color = textColor[label.cssClass] ?? "#000000";
  const container = document.createElement("span");
  const pill = document.createElement("span");
  pill.className = "po-detail-grid-urgency-pill";
  pill.style.background = bg;
  pill.style.color = color;
  pill.textContent = `${label.emoji} ${label.text}`;
  container.append(pill);
  container.append(` ${String(weeks)} week${weeks === 1 ? "" : "s"}`);
  return container;
}

/* ── Column Definitions ── */

const columnDefs = computed<ColDef[]>(() => {
  const defs: ColDef[] = [
    {
      field: "sku",
      headerName: "SKU",
      cellRenderer: (parameters: { value: string }) => {
        const strongEl = document.createElement("strong");
        strongEl.textContent = parameters.value;
        return strongEl;
      },
      flex: 0.8,
      minWidth: 70,
      headerTooltip: "SKU",
    },
    {
      field: "collection",
      headerName: "Collection",
      flex: 0.9,
      minWidth: 72,
      headerTooltip: "Collection",
    },
    {
      field: "description",
      headerName: "Description",
      flex: 2,
      minWidth: 120,
      headerTooltip: "Description",
    },
    {
      field: "color",
      headerName: "Color",
      flex: 0.8,
      minWidth: 65,
      headerTooltip: "Color",
    },
    {
      field: "quantityAvailable",
      headerName: "QOH",
      flex: 0.6,
      minWidth: 60,
      headerTooltip:
        "Warehouse on-hand used for expected inventory at arrival (Inventory Flow when that source ran).",
    },
    {
      field: "forecastQuantityAvailable",
      headerName: "Forecast",
      flex: 0.7,
      minWidth: 65,
      headerTooltip:
        "Expected warehouse units at arrival excluding this PO (on-hand + other inbound − sell-through). Same extra units the algorithm scored.",
    },
    {
      field: "adjustedForecast",
      headerName: "Adj Forecast",
      flex: 0.8,
      minWidth: 72,
      headerTooltip:
        "Expected units at arrival plus this PO's quantity after redistribution — the weeks-of-stock numerator.",
    },
    {
      field: "dailyRateOfSale",
      headerName: "RoS",
      flex: 0.7,
      minWidth: 60,
      headerTooltip:
        "Daily rate of sale the algorithm used for this run (Inventory Flow campus ROS, PO Release forecast, or trailing SQL — whichever demand source was selected).",
      valueFormatter: (params) => {
        const value = params.value as unknown;
        return typeof value === "number" && Number.isFinite(value)
          ? value.toFixed(2)
          : "—";
      },
    },
  ];

  if (showPoDetailPreviousValues) {
    defs.push({
      field: "previousQuantity",
      headerName: "Prev Order",
      flex: 0.8,
      minWidth: 70,
      headerTooltip:
        "Order quantity before this SKU's redistribution adjustment",
    });
  }

  defs.push(
    {
      field: "quantity",
      headerName: "Order",
      flex: 0.7,
      minWidth: 60,
      headerTooltip: "Order quantity after redistribution",
    },
    {
      field: "difference",
      headerName: "Difference",
      headerTooltip: "Difference",
      flex: 0.7,
      minWidth: 65,
      cellRenderer: (parameters: { value: number }) => {
        const value = parameters.value;
        const span = document.createElement("span");
        if (value > 0) {
          span.className = "po-detail-grid-change-gain";
          span.textContent = `+${String(value)}`;
        } else if (value < 0) {
          span.className = "po-detail-grid-change-loss";
          span.textContent = String(value);
        } else {
          span.className = "dashboard-text-secondary";
          span.textContent = "—";
        }
        return span;
      },
    }
  );

  if (showPoDetailPreviousValues) {
    defs.push({
      field: "previousWeeksOfStock",
      headerName: "Prev Weeks of Stock",
      headerTooltip:
        "Weeks of stock before this SKU's redistribution adjustment. Expected warehouse units at arrival plus this PO's previous quantity, divided by weekly rate of sale.",
      flex: 1,
      minWidth: 95,
      cellRenderer: (parameters: { value: number; data: PoDetailGridSkuRow }) =>
        renderWeeksOfStockCell(
          parameters.value,
          parameters.data.dailyRateOfSale,
          parameters.data.previousDaysToStockout
        ),
    });
  }

  defs.push({
    field: "weeksOfStock",
    headerName: "Weeks of Stock",
    headerTooltip:
      "Weeks of stock after redistribution: expected warehouse units at arrival plus this PO's quantity, divided by weekly rate of sale.",
    flex: 1,
    minWidth: 95,
    cellRenderer: (parameters: { value: number; data: PoDetailGridSkuRow }) =>
      renderWeeksOfStockCell(
        parameters.value,
        parameters.data.dailyRateOfSale,
        parameters.data.daysToStockout
      ),
  });

  return defs;
});

const defaultColDef: ColDef = {
  ...FITTABLE_DEFAULT_COL_DEF,
  sortable: false,
  filter: false,
};
</script>

<template>
  <div class="flex h-full min-h-0 min-w-0 flex-1 flex-col">
    <div
      v-for="(po, index) in purchaseOrders"
      :key="'pod-' + po.purchaseOrderNumber"
      class="po-detail-grid-card"
    >
      <div class="po-detail-grid-card-header">
        <h2>
          {{ po.purchaseOrderNumber }} — {{ formatDate(po.arrivalDate) }}
          <span class="dashboard-text-secondary font-normal">
            ({{ daysBetween(po.arrivalDate) }} days out)
          </span>
        </h2>
        <div>
          <span class="dashboard-text-secondary text-sm">
            SKUs: {{ po.countUniqueSkus }} · Items: {{ po.sumQuantities }} · PO
            Cube: {{ parseFloat(po.cubeTotal.toFixed(2)) }}
          </span>
        </div>
      </div>
      <AppGrid
        :id="`po-detail-grid-${String(index)}`"
        :column-defs="columnDefs"
        :row-data="getRowData(po.purchaseOrderNumber)"
        :default-col-def="defaultColDef"
        :max-rows="5"
        :min-grid-height="40"
        :no-rows-overlay-component-params="{
          getTitle: () => {
            return 'No changes needed on this PO';
          },
        }"
      />
    </div>
  </div>
</template>
